import { forwardRef, useEffect, useImperativeHandle } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { isEmpty, noop } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import components
import ValidatableDropdown, { type ValidatableDropdownProps } from 'components/common/ValidatableDropdown';
// Import constants
import { l } from 'constants/common';

interface Props extends ValidatableDropdownProps {
    data?: { label: string; value: string | number }[];
    value?: string | number;
    onChange?: (selectedValue: string | number) => void;
    placeholder?: string;
    disabled?: boolean;
}

export interface SingleDropdownFormRef {
    submitForm: () => Promise<boolean>;
    reset: () => void;
}

const SingleDropdownForm = forwardRef<SingleDropdownFormRef, Props>((props, ref) => {
    const { disabled, data, value, onChange, placeholder, ...rest } = props;
    const { t } = useTranslation();

    const formik = useFormik({
        initialValues: {
            selected: null,
        },
        onSubmit: noop,
        validationSchema: Yup.object({
            selected: Yup.string()
                .required(t(l.CannotBeEmpty, { fieldName: placeholder || l.Value }))
                .nullable(true),
        }),
    });

    const { values, setFieldValue, errors, touched, submitForm, validateForm, setFieldTouched } = formik;

    useImperativeHandle(ref, () => ({
        submitForm() {
            return new Promise((resolve, reject) => {
                submitForm()
                    .then(() =>
                        validateForm(values).then((formErrors) => {
                            resolve(isEmpty(formErrors));
                            return;
                        }),
                    )
                    .catch(() => reject());
            });
        },
        reset() {
            setFieldTouched('selected', false);
        },
    }));

    const onChangeValue = (e) => {
        setFieldValue('selected', e?.value);
        onChange?.(e);
    };

    useEffect(() => {
        setFieldValue('selected', value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <ValidatableDropdown
            isMultiSelect={false}
            disabled={disabled}
            hasSearch={true}
            inputSize="default"
            labelAppearance="swap"
            value={value}
            data={data}
            onChange={onChangeValue}
            searchPlaceholderText={t(l.Search)}
            noDataText={t(l.NoDataFound)}
            placeholder={placeholder}
            isValid={!(touched.selected && errors.selected)}
            errorText={touched.selected && errors.selected}
            {...rest}
        />
    );
});

SingleDropdownForm.displayName = 'SingleDropdownForm';

export default SingleDropdownForm;
